import { Injectable, signal } from '@angular/core';

const TOPUP_REF_PARAM = 'topupRef';

/**
 * Holds the Vipps top-up reference across the return trip from vipps.no.
 *
 * AppComponent.loadApplication() unconditionally navigates to /dashboard and wipes the
 * query string, so `capture()` must run before that (see app.component.ts ngOnInit),
 * and the reference is stashed here rather than read from the URL again later.
 */
@Injectable({ providedIn: 'root' })
export class TopupReturnService {
	private readonly _pendingReference = signal<string | null>(null);
	readonly pendingReference = this._pendingReference.asReadonly();

	/** Reads `topupRef` from the current URL, stores it, and strips it from the address
	 * bar so a manual refresh doesn't re-trigger the auto-reopen flow. */
	capture(): void {
		const params = new URLSearchParams(window.location.search);
		const reference = params.get(TOPUP_REF_PARAM);
		if (!reference) return;

		this._pendingReference.set(reference);

		params.delete(TOPUP_REF_PARAM);
		const query = params.toString();
		const strippedUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
		window.history.replaceState(null, '', strippedUrl);
	}

	hasPending(): boolean {
		return this._pendingReference() !== null;
	}

	/** Returns and clears the pending reference — single-consume so a remount doesn't re-trigger. */
	consume(): string | null {
		const reference = this._pendingReference();
		this._pendingReference.set(null);
		return reference;
	}
}
